/**
 * Server fallback adapter (implementation-plan.md §2.4) — the `callout-render` Worker.
 *
 * Selected only when the capability probe found no usable H.264 encoder (§2.3). The Worker
 * runs ffmpeg server-side (no GPL obligation, no client download, no iOS crash) and returns a
 * rendered MP4 in R2.
 *
 * Phase 1 status: the Worker's `/api/render` is a real, authenticated endpoint that answers
 * `not_implemented` with "Server rendering is not yet available". This adapter exists so the
 * selection logic is complete and the user gets a clear message instead of a crash — the
 * ffmpeg implementation is Phase 2.
 */
import { probeCapabilities } from './probe';
import type {
  CapabilityReport,
  ClipRenderer,
  RenderRequest,
  RenderResult,
} from './types';

export const RENDER_BASE = (
  import.meta.env.VITE_RENDER_BASE ?? 'http://127.0.0.1:8788'
).replace(/\/+$/, '');

export class ServerRenderingUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ServerRenderingUnavailableError';
  }
}

export interface ServerRenderRequestMeta {
  /** StructuredClip id, when the clip is already persisted. */
  clipId?: string;
  /** R2 key of the source footage. */
  footageKey?: string;
  /** Pre-signed URL of the source video, when the caller has one. */
  sourceUrl?: string;
  /** Auth token for the Worker. */
  token?: string | null;
}

export class ServerRenderer implements ClipRenderer {
  readonly kind = 'server' as const;

  constructor(private readonly meta: ServerRenderRequestMeta = {}) {}

  probe(): Promise<CapabilityReport> {
    // The server path is precisely what the probe reports as 'none'/'server' on the client.
    return probeCapabilities().then((report) => ({ ...report, renderer: 'server' as const }));
  }

  async render(request: RenderRequest): Promise<RenderResult> {
    const started = performance.now();
    const response = await fetch(`${RENDER_BASE}/api/render`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(this.meta.token ? { Authorization: `Bearer ${this.meta.token}` } : {}),
      },
      body: JSON.stringify({
        clipId: this.meta.clipId,
        footageKey: this.meta.footageKey,
        sourceUrl:
          this.meta.sourceUrl ??
          (typeof request.sourceVideo === 'string' ? request.sourceVideo : undefined),
        tIn: request.tIn,
        tOut: request.tOut,
        quality: request.quality,
        overlay: {
          label: request.overlay.title,
          primaryColor: request.overlay.primaryColor,
          secondaryColor: request.overlay.secondaryColor,
          clubName: request.overlay.clubName,
          logoUrl: request.overlay.logoUrl,
        },
      }),
    });

    const text = await response.text();
    const payload = text ? (JSON.parse(text) as { error?: { code?: string; message?: string } }) : null;

    if (response.status === 501 || payload?.error?.code === 'not_implemented') {
      throw new ServerRenderingUnavailableError(
        payload?.error?.message ?? 'Server rendering is not yet available',
      );
    }
    if (!response.ok) {
      throw new Error(
        payload?.error?.message ?? `Server render failed (${response.status})`,
      );
    }

    // Phase 2: the Worker returns a result key; the client fetches the object from R2.
    const result = payload as unknown as { resultStorageKey?: string; durationSeconds?: number };
    const url = result.resultStorageKey
      ? `${RENDER_BASE}/media/${result.resultStorageKey}`
      : null;
    if (!url) {
      throw new ServerRenderingUnavailableError('Server rendering is not yet available');
    }
    const blob = await (await fetch(url)).blob();
    return {
      blob,
      duration: result.durationSeconds ?? request.tOut - request.tIn,
      sizeBytes: blob.size,
      renderTimeMs: Math.round(performance.now() - started),
      renderer: 'server',
      audioTrack: request.audioTrack ?? 'reencode',
      videoCodec: null,
      width: 0,
      height: 0,
    };
  }
}
